import React from 'react';
import { useTranslation } from '@shared/core';
import { Category } from '@graphql/generated';
import { BoxProps, Flex, TextV2 } from '@withjoy/joykit';
import { AccommodationCombined } from '../../Accommodations/Accommodation.types';
import { createDateFromUnformattedString } from '@shared/utils/dateHelpers';
import { GuestSiteTypographyOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride';
import {
  body1ToParagraphOverride,
  hed2ToSubHeadingOverride,
  hed4ToSubHeadingOverride
} from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.constants';
import { useFeatureValue } from '@shared/core/featureFlags';
import { useScaleGuestSiteFontSize } from '@apps/guest/components/GuestSiteTypographyOverride/hooks/useScaleGuestSiteFontSize';
import { pxToRem } from '@withjoy/joykit/theme';

interface RoomBlockPricingNonGridProps extends BoxProps {
  accommodation: AccommodationCombined;
}

export const RoomBlockPricingNonGrid = ({ accommodation, ...boxProps }: RoomBlockPricingNonGridProps) => {
  const { strikeoutPricePerNight, pricePerNight, cutoffDate } = accommodation;
  const fontPackExperimentEnabled = useFeatureValue('fontPackExperiment').value === 'treatment';
  const { scaleFontSize } = useScaleGuestSiteFontSize();
  const accTrans = useTranslation('guestSiteCustom').t('accommodations');

  return (
    <Flex justifyContent="space-between" alignItems="center" width="100%" borderTop="1px solid" borderTopColor="mono3" paddingTop={4} {...boxProps}>
      {cutoffDate && (
        <Flex flexDirection="column" gap="1">
          <GuestSiteTypographyOverride override={body1ToParagraphOverride} defaultFontFamily="Inter UI">
            <TextV2
              typographyVariant={'body1'}
              {...(!fontPackExperimentEnabled && { fontFamily: 'Inter UI' })}
              fontSize={scaleFontSize(pxToRem(15), Category.PARAGRAPH)}
              color="mono14"
              fontWeight={400}
              letterSpacing="-0.15px"
            >
              {accTrans.bookBefore()}
            </TextV2>
          </GuestSiteTypographyOverride>
          <GuestSiteTypographyOverride override={body1ToParagraphOverride} defaultFontFamily="Inter UI">
            <TextV2
              typographyVariant={'body1'}
              {...(!fontPackExperimentEnabled && { fontFamily: 'Inter UI' })}
              fontSize={scaleFontSize(pxToRem(15), Category.PARAGRAPH)}
              color="mono14"
              fontWeight={400}
              letterSpacing="-0.15px"
            >
              {createDateFromUnformattedString(cutoffDate)?.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' })}
            </TextV2>
          </GuestSiteTypographyOverride>
        </Flex>
      )}
      <Flex flexDirection="column" gap="1">
        <GuestSiteTypographyOverride override={hed4ToSubHeadingOverride} defaultFontFamily="Inter UI">
          <TextV2 typographyVariant={'body1'} color="mono14" fontWeight={400} textAlign="right">
            {accTrans.groupRate()}
          </TextV2>
        </GuestSiteTypographyOverride>
        <Flex gap={2} justifyContent="flex-end">
          {!!strikeoutPricePerNight && (
            <GuestSiteTypographyOverride override={hed4ToSubHeadingOverride} defaultFontFamily="Inter UI">
              <TextV2
                typographyVariant={'hed4'}
                {...(!fontPackExperimentEnabled && { fontFamily: 'Inter UI' })}
                fontSize={scaleFontSize(pxToRem(18), Category.SUB_HEADING)}
                color="mono10"
                fontWeight={600}
                textAlign="right"
                backgroundSize={'100% 1.5px'}
                width={'auto'}
                backgroundImage={'linear-gradient(currentColor, currentColor)'}
                backgroundPosition={'0% 50%'}
                backgroundRepeat={'no-repeat'}
              >
                ${strikeoutPricePerNight.toString()}
              </TextV2>
            </GuestSiteTypographyOverride>
          )}
          <GuestSiteTypographyOverride override={hed2ToSubHeadingOverride} defaultFontFamily="Inter UI">
            <TextV2
              typographyVariant={'hed2'}
              {...(!fontPackExperimentEnabled && { fontFamily: 'Inter UI' })}
              fontSize={scaleFontSize(pxToRem(17), Category.SUB_HEADING)}
              color="mono14"
              fontWeight={600}
              textAlign="right"
              letterSpacing="-0.272px"
              textTransform="none"
            >
              ${accTrans.pricePerNight({ price: pricePerNight })}
            </TextV2>
          </GuestSiteTypographyOverride>
        </Flex>
      </Flex>
    </Flex>
  );
};
